import React from 'react';
import { Grid } from '@mui/material';
import { endOfDay } from 'date-fns';

import {
    AutocompleteField,
    NumberField,
    TextField,
    DateTimeField,
    BoolField
} from '../CommonComponents/Form';
import { SectionDivider } from '../CommonComponents';
import OrderDate from '../Orders/OrderFormComponents/OrderDate';
import OrderLocation from '../Orders/OrderFormComponents/OrderLocation';

import { fetchAllGroups, selectMyGroups } from '../../redux/actions/groups';
import { useDispatch, useSelector } from '../../redux/hooks';
import { formatDate } from '../../utils/parsing';

type PropsType = {
    entity: { [key: string]: any },
    onChange: (e: { [key: string]: any }) => void,
    skipRequiredValidation?: boolean
};

export default function CapacityFormFields(props: PropsType) {
    const { onChange, entity, skipRequiredValidation } = props;

    const dispatch = useDispatch();
    const groups = useSelector(selectMyGroups());

    const fetchGroups = React.useCallback(() => {
        dispatch(fetchAllGroups());
    }, [dispatch]);

    return (
        <>
            <Grid container spacing={{ xs: 2, md: 4 }}>
                <Grid item xs={12} md={6}>
                    <SectionDivider label="Startdatum" marginTop={0} />
                    <OrderDate
                        partialKey="pickup"
                        partialName="start"
                        entity={entity}
                        onChange={onChange}
                        skipRequiredValidation={skipRequiredValidation}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <SectionDivider label="Måldatum" marginTop={0} />
                    <OrderDate
                        partialKey="delivery"
                        partialName="mål"
                        entity={entity}
                        onChange={onChange}
                        skipRequiredValidation={skipRequiredValidation}
                        firstMinDate={entity.firstPickupDate ? entity.firstPickupDate : undefined}
                        lastMinDate={entity.lastPickupDate ? entity.lastPickupDate : undefined}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={{ xs: 2, md: 4 }}>
                <Grid item xs={12} md={6}>
                    <SectionDivider label="Startplats" />
                    <OrderLocation
                        partialKey="pickup"
                        partialName="start"
                        entity={entity}
                        onChange={onChange}
                        skipRequiredValidation={skipRequiredValidation}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <SectionDivider label="Målplats" />
                    <OrderLocation
                        partialKey="delivery"
                        partialName="mål"
                        entity={entity}
                        onChange={onChange}
                        skipRequiredValidation={skipRequiredValidation}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={{ xs: 2, md: 4 }}>
                <Grid item xs={12} md={6}>
                    <SectionDivider label="Ledig kapacitet" />

                    <Grid container spacing={2}>
                        <Grid item xs={12} lg={6}>
                            <NumberField
                                name="grossWeight"
                                label="Totalvikt [kg]"
                                value={entity.grossWeight}
                                onChange={onChange}
                                minValue={0}
                                required={!skipRequiredValidation}
                            />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <NumberField
                                name="chargeableWeight"
                                label="FDR-vikt [kg]"
                                value={entity.chargeableWeight}
                                onChange={onChange}
                                minValue={0}
                            />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <NumberField
                                name="loadingMeters"
                                label="Flakmeter"
                                value={entity.loadingMeters}
                                onChange={onChange}
                                minValue={0}
                            />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <NumberField
                                name="volume"
                                label="Volym [m³]"
                                value={entity.volume}
                                onChange={onChange}
                                minValue={0}
                            />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <NumberField
                                name="palletPlaces"
                                label="Pallplatser"
                                value={entity.palletPlaces}
                                onChange={onChange}
                                minValue={0}
                            />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <NumberField
                                name="pallets"
                                label="Pallar"
                                value={entity.pallets}
                                onChange={onChange}
                                minValue={0}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                name="publicDescription"
                                label="Publik beskrivning"
                                value={entity.publicDescription}
                                onChange={onChange}
                                helperText="Här kan du beskriva din bil, vilken typ av gods du kan ta eller andra förutsättningar"
                            />
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs={12} md={6}>
                    <SectionDivider label="Publicering" />
                    <Grid container spacing={2}>
                        <Grid item xs={12} lg={6}>
                            <BoolField
                                name="isPublic"
                                label="Publicera publikt"
                                value={entity.isPublic}
                                onChange={({ isPublic }) => onChange({ isPublic, groups: [] })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <AutocompleteField<{ id: number, name: string, ownerTenantName?: string }>
                                label="Publicera i grupp"
                                helperText="Välj grupper eller publicera publikt"
                                optionIdKey="id"
                                fetchOptions={fetchGroups}
                                options={groups.filter(
                                    (o: { isOwner: boolean; canPublish: boolean; }) => (o.isOwner || o.canPublish)
                                )}
                                getOptionName={(o) => o.name}
                                getOptionDescription={(o) => o.ownerTenantName || ''}
                                onChange={(g) => onChange({ groups: g, isPublic: g.length > 0 ? false : undefined })}
                                fetchOnlyOnce
                                displayOption={entity.groups || []}
                                multiple
                                required={!skipRequiredValidation && !entity.isPublic}
                            />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <DateTimeField
                                name="expiresAt"
                                label="Föreslå uppdrag senast"
                                value={entity.expiresAt}
                                onChange={onChange}
                                minDateTime={new Date()}
                                maxDateTime={
                                    entity.lastDeliveryDate || entity.firstDeliveryDate
                                        ? endOfDay(new Date(entity.lastDeliveryDate || entity.firstDeliveryDate))
                                        : undefined
                                }
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
}
